import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, Activity, AlertTriangle, ShieldAlert, Wrench, Users,
  GraduationCap, Briefcase, CloudRain, Clock, ClipboardCheck,
  TrendingUp, TrendingDown, BarChart3, Zap,
} from "lucide-react";import { MasciLogo } from "@/components/MasciLogo";
import RoleWelcomeStrip from "@/components/RoleWelcomeStrip";
import OperationalActivityTicker from "@/components/OperationalActivityTicker";
import OperationalPressurePanel from "@/components/OperationalPressurePanel";
import WorkflowChainPanel from "@/components/WorkflowChainPanel";
import RolePainReliefStrip from "@/components/RolePainReliefStrip";
import DemoBanner from "@/components/DemoBanner";
import { useT } from "@/lib/i18n";

/**
 * ExecutiveCommand
 * --------------------------------------------------
 * Owner / Executive operational intelligence layer. Mission-critical
 * visibility designed so a contractor's owner walks in at 7am, glances
 * at this single page, and knows everything — every active job, every
 * unresolved issue, every escalation, every operational pressure point.
 *
 * Restrained, dense, industrial. Same atmosphere as DemoLanding.
 * No SaaS chrome, no dashboard widgets — operational tiles, KPI rows,
 * heatmaps, and active escalation chains.
 */

const PROJECT_HEALTH = [
  {
    job: "JOB 2098",
    name: "I-45 Lane 2 · Houston",
    pm:   "PM · Carter",
    health: "ok",
    productionPct: 102,
    trend: "up",
    incidents: 0,
    overdue: 0,
    flags: [],
  },
  {
    job: "JOB 2104",
    name: "SR-22 Widening · MOT Phase 3",
    pm:   "PM · Diaz",
    health: "warn",
    productionPct: 87,
    trend: "down",
    incidents: 1,
    overdue: 2,
    flags: ["WEATHER HOLD", "MANPOWER -2"],
  },
  {
    job: "JOB 2110",
    name: "Municipal Utility · Trench Ops",
    pm:   "PM · Carter",
    health: "critical",
    productionPct: 71,
    trend: "down",
    incidents: 1,
    overdue: 4,
    flags: ["STOP-THE-LINE", "CA OPEN"],
  },
  {
    job: "JOB 2098B",
    name: "Asphalt Overlay · Phase 2",
    pm:   "PM · Diaz",
    health: "ok",
    productionPct: 98,
    trend: "flat",
    incidents: 0,
    overdue: 1,
    flags: [],
  },
];

const HEALTH_TONE = {
  ok:       { color: "#65DA76", label: "ON-TRACK" },
  warn:     { color: "#FFB000", label: "WATCH"    },
  critical: { color: "#FF6A6A", label: "RISK"     },
};

const TOP_KPIS = [
  { label: "Active jobs",         value: "4",    sub: "all routed",                 tone: "ok" },
  { label: "Manpower on shift",   value: "48",   sub: "union + sub",                tone: "info" },
  { label: "Equipment OOS",       value: "5/8",  sub: "62% utilization",            tone: "warn" },
  { label: "Open incidents",      value: "2",    sub: "1 stop-the-line",            tone: "critical" },
  { label: "Production · today",  value: "94%",  sub: "vs forecast",                tone: "ok" },
  { label: "Workforce readiness", value: "87%",  sub: "certs current",              tone: "ok" },
];

const RISK_HEAT_BASE = [
  { area: "Safety",          score: 72, items: "1 stop-the-line · 4 open CAs",          tone: "warn" },
  { area: "Schedule",        score: 88, items: "JOB 2104 · weather hold 38m",            tone: "warn" },
  { area: "Cost",            score: 91, items: "Material variance +3.2%",                tone: "ok"   },
  { area: "Compliance",      score: 94, items: "OSHA-30 · 3 ops expiring 14d",           tone: "ok"   },
  { area: "Equipment",       score: 68, items: "5 OOS · CAT 336 lockout",                tone: "warn" },
  { area: "Manpower",        score: 81, items: "JOB 2110 · Crew A-1 short -2",           tone: "warn" },
];

const ESCALATIONS = [
  { id: "INC-2042", title: "Trench box damage · stop-the-line",   actor: "Safety Lead",   level: "Owner-visible", meta: "12s ago · CA queued · training auto-assigned", sev: "critical" },
  { id: "INC-2043", title: "Pre-Op fail · CAT 336",                actor: "Shop Mgr",      level: "PM-visible",     meta: "07:42 · awaiting RTS · ETA 11:30",            sev: "warn"     },
  { id: "APV-1187", title: "Daily Report · 4 reports · 22h aged", actor: "PM Diaz",       level: "PM-action",      meta: "Auto-escalates 09:45 if not approved",         sev: "warn"     },
  { id: "CRT-9911", title: "OSHA-30 expiring · 3 operators",       actor: "Training Auto", level: "HR-visible",     meta: "Re-cert window opens · auto-assigned EN+ES",   sev: "info"     },
];

const SEV = {
  critical: "#FF6A6A",
  warn:     "#FFB000",
  info:     "#FF6A00",
  ok:       "#65DA76",
};

export default function ExecutiveCommand() {
  const { t } = useT();
  const [now, setNow] = useState(new Date());
  // Live risk-heat deltas. Real submissions adjust the matching area
  // score for 8s, then it decays back to the seeded baseline. No
  // animation beyond a 320ms ease on the bar width and color.
  const [riskDeltas, setRiskDeltas] = useState({});       // {areaIdx: signedInt}
  const [riskPulse, setRiskPulse]   = useState(null);     // area idx currently flashing tone color
  useEffect(() => {
    const i = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(i);
  }, []);

  // Listen for live submissions and ripple them into the heat-map.
  // Mapping: incident → Safety (-6) · preop-fail → Equipment (-5) ·
  // toolbox-talk → Compliance (+3) and Safety (+2) · daily-report →
  // Schedule (+2). Auto-decay after 8s.
  useEffect(() => {
    const decayTimers = [];
    const onSubmit = (ev) => {
      const kind = (ev?.detail?.kind || "").toLowerCase();
      const bumps = [];
      if (kind === "incident" || kind === "near-miss") {
        bumps.push({ idx: 0, delta: -6 }); // Safety
      } else if (kind === "preop-fail") {
        bumps.push({ idx: 4, delta: -5 }); // Equipment
      } else if (kind === "toolbox-talk") {
        bumps.push({ idx: 0, delta: +2 }); // Safety
        bumps.push({ idx: 3, delta: +3 }); // Compliance
      } else if (kind === "daily-report") {
        bumps.push({ idx: 1, delta: +2 }); // Schedule
      }
      if (bumps.length === 0) return;
      bumps.forEach(({ idx, delta }) => {
        setRiskDeltas((d) => ({ ...d, [idx]: (d[idx] || 0) + delta }));
        setRiskPulse(idx);
        const t = setTimeout(() => {
          setRiskDeltas((d) => {
            const cur = (d[idx] || 0) - delta;
            const next = { ...d };
            if (Math.abs(cur) < 0.01) delete next[idx]; else next[idx] = cur;
            return next;
          });
          setRiskPulse((cur) => (cur === idx ? null : cur));
        }, 8000);
        decayTimers.push(t);
      });
    };
    window.addEventListener("forgeops:submission", onSubmit);
    return () => {
      window.removeEventListener("forgeops:submission", onSubmit);
      decayTimers.forEach(clearTimeout);
    };
  }, []);

  // Derived heat-map with deltas applied + tone re-evaluation.
  const RISK_HEAT = RISK_HEAT_BASE.map((r, i) => {
    const delta = riskDeltas[i] || 0;
    const score = Math.max(0, Math.min(100, r.score + delta));
    const tone = score < 75 ? "warn" : score < 60 ? "critical" : "ok";
    return { ...r, score, delta, tone };
  });

  return (
    <div className="min-h-screen" style={{ background: "#0A0A0A", color: "#FFFFFF" }}>
      <DemoBanner />
      <div className="caution-stripe" />

      {/* Header */}
      <header style={{ background: "#0B0B0B", borderBottom: "1px solid #1A1A1A" }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between gap-3 flex-wrap">
          <Link
            to="/"
            className="inline-flex items-center text-white text-xs font-bold uppercase tracking-[0.25em] hover:text-orange-400 transition-colors"
            data-testid="exec-back-platform"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Platform
          </Link>
          <MasciLogo variant="mark" size="md" homeLink="/" />
          <Link
            to="/admin"
            className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.25em] font-bold h-9 px-3 rounded transition-colors"
            style={{ background: "#1A1A1A", color: "#FFFFFF", border: "1px solid #2A2A2A" }}
            data-testid="exec-open-admin"
          >
            Open Admin Console →
          </Link>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-8 sm:py-10 space-y-10">
        <RoleWelcomeStrip />

        {/* Hero */}
        <section className="relative">
          <div
            aria-hidden
            className="absolute pointer-events-none"
            style={{
              top: -40, left: -40, width: 320, height: 320, borderRadius: 9999,
              filter: "blur(110px)",
              background: "radial-gradient(circle, rgba(255,106,0,0.18) 0%, rgba(255,106,0,0) 70%)",
            }}
          />
          <div className="relative">
            <div className="font-mono text-[11px] uppercase tracking-[0.4em] mb-3 flex items-center gap-2"
                 style={{ color: "#FF6A00" }}>
              <Zap className="w-3.5 h-3.5" />
              {t("Executive · Operations Command")}
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-white max-w-4xl leading-[1.05]">
              {t("See your company.")}{" "}
              <span style={{ color: "#FF6A00" }}>
                {t("Every job. Every issue. Every escalation.")}
              </span>
            </h1>
            <p className="text-base sm:text-lg mt-4 max-w-2xl leading-relaxed" style={{ color: "#BDBDBD" }}>
              Owner-level visibility built for 7am decisions. Operational
              pressure, project health, risk areas, and active escalation
              chains — visible the moment you sign in.
            </p>
            <div className="mt-5 flex items-center gap-3 flex-wrap">
              <span className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] font-bold"
                    style={{ color: "#65DA76" }}>
                <span className="w-2 h-2 rounded-full" style={{ background: "#65DA76", boxShadow: "0 0 8px #65DA76" }} />
                System Healthy · Routing Live
              </span>
              <span className="font-mono text-[10px] uppercase tracking-[0.25em]" style={{ color: "#666" }}>
                {now.toLocaleString([], { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false })} · 3 active jobs · 47 ops
              </span>
            </div>
          </div>
        </section>

        {/* Top KPI strip */}
        <section
          className="grid grid-cols-2 lg:grid-cols-6 gap-px rounded-md overflow-hidden"
          style={{ background: "#1A1A1A", border: "1px solid #1A1A1A" }}
          data-testid="exec-kpi-strip"
        >
          {TOP_KPIS.map((k, i) => (
            <KpiCell key={i} {...k} />
          ))}
        </section>

        {/* Live activity */}
        <OperationalActivityTicker />

        {/* Project Health table — the core executive view */}
        <section
          className="rounded-md overflow-hidden"
          style={{ background: "#0A0A0A", border: "1px solid #1A1A1A" }}
          data-testid="exec-project-health"
        >
          <div
            className="px-5 sm:px-7 py-4 border-b flex items-center justify-between flex-wrap gap-3"
            style={{ borderColor: "#1A1A1A", background: "#0B0B0B" }}
          >
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] font-bold mb-1"
                   style={{ color: "#FF6A00" }}>
                Project Health · Live
              </div>
              <h3 className="font-display text-xl sm:text-2xl font-black tracking-tight text-white">
                Every active job. One row at a time.
              </h3>
            </div>
            <span className="font-mono text-[10px] uppercase tracking-[0.25em]"
                  style={{ color: "#666" }}>
              4 active · last sync {now.toLocaleTimeString([], { hour12: false })}
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr
                  className="font-mono text-[9px] uppercase tracking-[0.25em] text-left"
                  style={{ background: "#0F0F0F", color: "#666" }}
                >
                  <th className="px-4 py-3 font-bold">Job</th>
                  <th className="px-4 py-3 font-bold">PM</th>
                  <th className="px-4 py-3 font-bold">Health</th>
                  <th className="px-4 py-3 font-bold text-right">Production</th>
                  <th className="px-4 py-3 font-bold text-right">Incidents</th>
                  <th className="px-4 py-3 font-bold text-right">Overdue</th>
                  <th className="px-4 py-3 font-bold">Flags</th>
                </tr>
              </thead>
              <tbody>
                {PROJECT_HEALTH.map((p, i) => {
                  const tone = HEALTH_TONE[p.health];
                  const Trend = p.trend === "up" ? TrendingUp : p.trend === "down" ? TrendingDown : Activity;
                  return (
                    <tr
                      key={i}
                      style={{ borderTop: "1px solid #1A1A1A" }}
                      className="hover:bg-[#0F0F0F] transition-colors"
                      data-testid={`exec-project-row-${p.job.toLowerCase().replace(/\s/g,'-')}`}
                    >
                      <td className="px-4 py-3.5">
                        <div className="font-mono text-[10px] font-bold uppercase tracking-[0.2em]" style={{ color: "#FF6A00" }}>
                          {p.job}
                        </div>
                        <div className="font-display text-sm font-bold text-white mt-0.5">
                          {p.name}
                        </div>
                      </td>
                      <td className="px-4 py-3.5 font-mono text-[11px]" style={{ color: "#BDBDBD" }}>
                        {p.pm}
                      </td>
                      <td className="px-4 py-3.5">
                        <span
                          className="inline-flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.2em] font-bold px-2 py-1 rounded"
                          style={{
                            background: `${tone.color}1F`,
                            color: tone.color,
                            border: `1px solid ${tone.color}55`,
                          }}
                        >
                          <span className="w-1.5 h-1.5 rounded-full" style={{ background: tone.color }} />
                          {tone.label}
                        </span>
                      </td>
                      <td className="px-4 py-3.5 text-right">
                        <span className="inline-flex items-center gap-1.5 font-display font-black text-base" style={{ color: p.productionPct >= 95 ? "#65DA76" : p.productionPct >= 80 ? "#FFB000" : "#FF6A6A" }}>
                          <Trend className="w-3.5 h-3.5" />
                          {p.productionPct}%
                        </span>
                      </td>
                      <td className="px-4 py-3.5 text-right font-display font-black text-base" style={{ color: p.incidents > 0 ? "#FF6A6A" : "#666" }}>
                        {p.incidents}
                      </td>
                      <td className="px-4 py-3.5 text-right font-display font-black text-base" style={{ color: p.overdue > 2 ? "#FF6A6A" : p.overdue > 0 ? "#FFB000" : "#666" }}>
                        {p.overdue}
                      </td>
                      <td className="px-4 py-3.5">
                        <div className="flex flex-wrap gap-1">
                          {p.flags.length === 0 ? (
                            <span className="font-mono text-[10px]" style={{ color: "#666" }}>—</span>
                          ) : (
                            p.flags.map((f, j) => (
                              <span
                                key={j}
                                className="font-mono text-[9px] uppercase tracking-[0.2em] font-bold px-1.5 py-0.5 rounded"
                                style={{
                                  background: "#1A1A1A",
                                  color: "#FF6A6A",
                                  border: "1px solid #FF6A6A55",
                                }}
                              >
                                {f}
                              </span>
                            ))
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>

        {/* Risk heat-map */}
        <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div
            className="rounded-md overflow-hidden"
            style={{ background: "#0A0A0A", border: "1px solid #1A1A1A" }}
            data-testid="exec-risk-heatmap"
          >
            <div className="px-5 py-4 border-b" style={{ borderColor: "#1A1A1A", background: "#0B0B0B" }}>
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] font-bold" style={{ color: "#FF6A00" }}>
                {t("Risk · Operational Heat-Map")}
              </div>
              <h3 className="font-display text-lg font-black text-white mt-1">
                Pressure points across the operation.
              </h3>
            </div>
            <div className="p-4 sm:p-5 space-y-2.5">
              {RISK_HEAT.map((r, i) => {
                const accent = SEV[r.tone];
                const isPulsing = riskPulse === i;
                return (
                  <div
                    key={i}
                    className="flex items-center gap-3"
                    data-testid={`risk-row-${r.area.toLowerCase()}`}
                  >
                    <div className="w-24 shrink-0">
                      <div className="font-mono text-[10px] uppercase tracking-[0.2em] font-bold text-white">
                        {r.area}
                      </div>
                    </div>
                    <div
                      className="flex-1 h-2 rounded-full overflow-hidden"
                      style={{ background: "#1A1A1A" }}
                    >
                      <div
                        className="h-full"
                        style={{
                          width: `${r.score}%`,
                          background: accent,
                          boxShadow: isPulsing ? `0 0 8px ${accent}` : `0 0 6px ${accent}80`,
                          transition: "width 320ms ease, background 320ms ease, box-shadow 320ms ease",
                        }}
                      />
                    </div>
                    <div
                      className="w-16 text-right font-mono font-bold text-sm shrink-0 flex items-center justify-end gap-1"
                      style={{ color: accent }}
                    >
                      {r.score}
                      {r.delta !== 0 && (
                        <span
                          className="font-mono text-[9px] uppercase tracking-[0.15em] font-bold"
                          style={{
                            color: r.delta < 0 ? "#FF6A6A" : "#65DA76",
                          }}
                          data-testid={`risk-delta-${r.area.toLowerCase()}`}
                        >
                          {r.delta > 0 ? `+${r.delta}` : r.delta}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
              <div className="mt-3 pt-3 border-t space-y-2" style={{ borderColor: "#1A1A1A" }}>
                {RISK_HEAT.map((r, i) => (
                  <div
                    key={i}
                    className="font-mono text-[10px] flex items-baseline gap-2"
                    style={{ color: "#BDBDBD" }}
                  >
                    <span style={{ color: "#666" }}>{r.area}:</span>
                    <span>{r.items}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Active Escalations */}
          <div
            className="rounded-md overflow-hidden"
            style={{ background: "#0A0A0A", border: "1px solid #1A1A1A" }}
            data-testid="exec-escalations"
          >
            <div className="px-5 py-4 border-b" style={{ borderColor: "#1A1A1A", background: "#0B0B0B" }}>
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] font-bold" style={{ color: "#FF6A00" }}>
                Active Escalation Chains
              </div>
              <h3 className="font-display text-lg font-black text-white mt-1">
                Items the system is actively routing right now.
              </h3>
            </div>
            <div className="p-4 sm:p-5 space-y-3">
              {ESCALATIONS.map((e, i) => {
                const accent = SEV[e.sev];
                return (
                  <div
                    key={i}
                    className="rounded-md p-3 flex items-start gap-3"
                    style={{ background: "#0F0F0F", borderLeft: `2px solid ${accent}` }}
                    data-testid={`escalation-${e.id.toLowerCase()}`}
                  >
                    <span
                      className="font-mono text-[10px] font-bold tracking-wide px-2 py-1 rounded shrink-0"
                      style={{
                        background: `${accent}1F`,
                        color: accent,
                        border: `1px solid ${accent}55`,
                      }}
                    >
                      {e.id}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="font-bold text-white text-sm leading-snug">
                        {e.title}
                      </div>
                      <div className="font-mono text-[10px] mt-1" style={{ color: "#8A8A8A" }}>
                        {e.actor} · {e.level}
                      </div>
                      <div className="font-mono text-[10px] mt-0.5" style={{ color: "#BDBDBD" }}>
                        {e.meta}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Pressure panel */}
        <OperationalPressurePanel />

        {/* Owner pain → relief */}
        <RolePainReliefStrip role="admin" />

        {/* Workflow chains */}
        <div>
          <div className="mb-5">
            <div className="font-mono text-[11px] uppercase tracking-[0.4em] mb-1" style={{ color: "#FF6A00" }}>
              System routing · simulations
            </div>
            <h3 className="font-display text-2xl font-black tracking-tight text-white">
              Watch how a single submission moves through the company.
            </h3>
          </div>
          <WorkflowChainPanel />
        </div>

        {/* Hand-off footer band */}
        <div
          className="p-5 sm:p-6 rounded-md flex items-center justify-between gap-5 flex-wrap"
          style={{ background: "#0F0F0F", border: "1px solid #1A1A1A" }}
        >
          <div className="flex items-center gap-3">
            <span
              className="inline-flex items-center justify-center w-12 h-12 rounded-md shrink-0"
              style={{
                background: "rgba(255,106,0,0.14)",
                color: "#FF6A00",
                border: "1px solid rgba(255,106,0,0.4)",
              }}
            >
              <BarChart3 className="w-6 h-6" />
            </span>
            <div>
              <div className="font-display font-black text-white">Drill down into any signal.</div>
              <div className="text-xs mt-0.5" style={{ color: "#BDBDBD" }}>
                The Admin Console is one click away. Same data — every record, every signature, every PDF.
              </div>
            </div>
          </div>
          <Link
            to="/admin"
            className="inline-flex items-center gap-2 h-12 px-5 font-mono text-[11px] uppercase tracking-[0.3em] font-black rounded transition-colors"
            style={{ background: "#FF6A00", color: "#000", border: "1px solid #FF6A00" }}
            data-testid="exec-open-admin-cta"
          >
            Open Admin Console →
          </Link>
        </div>
      </main>
    </div>
  );
}

function KpiCell({ label, value, sub, tone }) {
  const accent = SEV[tone] || "#FF6A00";
  return (
    <div className="p-4 sm:p-5" style={{ background: "#0F0F0F" }}>
      <div className="font-display text-3xl sm:text-4xl font-black tracking-tight leading-none" style={{ color: accent }}>
        {value}
      </div>
      <div className="font-mono text-[9px] uppercase tracking-[0.25em] mt-2 font-bold text-white">
        {label}
      </div>
      <div className="font-mono text-[10px] mt-1" style={{ color: "#666" }}>
        {sub}
      </div>
    </div>
  );
}
